import dayjs, { type Dayjs } from "dayjs"
import { compact, uniq } from "lodash"

import { type ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { setDayjsTimeToZero } from "#utils/dateHelpers"
import { addOneSemester } from "#utils/semesterHelpers"

/** How far past the filter date the makeup picker looks for candidates. */
export const MAKEUP_WINDOW_WEEKS = 2

export interface DateWindow {
  start: Dayjs
  end: Dayjs
}

/**
 * The candidate window for the makeup-session picker: filter date (start of
 * day) through +2 weeks (end of day). Null when no filter date is set — the
 * form only sets one once "schedule a makeup" is checked. An invalid date
 * falls back to `now` so a half-typed date never blanks the picker.
 */
export const getMakeupSessionWindow = (
  makeupFilterDate: Dayjs | null | undefined,
  now: Dayjs
): DateWindow | null => {
  if (!makeupFilterDate) return null
  const filterDate = makeupFilterDate.isValid() ? makeupFilterDate : now
  return {
    start: filterDate.startOf("day"),
    end: filterDate.add(MAKEUP_WINDOW_WEEKS, "week").endOf("day"),
  }
}

/**
 * `Form.useWatch` is `undefined` until the field registers, and `null` when
 * the picker is cleared. `??` treats that clear as missing and puts the saved
 * date back, so a deliberate clear would still query the old window.
 */
export const coalesceMakeupFilterDate = (
  watched: Dayjs | null | undefined,
  initial: Dayjs | null | undefined
): Dayjs | null | undefined => (watched === undefined ? initial : watched)

/**
 * Default "find sessions after" date.
 *
 * A booked makeup keeps its session's date so the picker window still
 * contains it — unless the stored filter date is null. Null is written when
 * the date is cleared or the makeup is only a planned absence, and the next
 * open starts at `now` so sessions run from that day forward. A past missed
 * session must not pin the window: the absence may have been recorded weeks
 * earlier.
 *
 * A brand-new form still uses today for the current semester and the missed
 * session's date for a prior-semester class, so a makeup recorded in the same
 * visit can see sessions around the absence.
 */
export const resolveMakeupFilterDate = ({
  makeupSessionStart,
  missedSessionStart,
  missedClassSemester,
  currentSemester,
  reopenWithoutSavedFilterDate,
  now,
}: {
  makeupSessionStart: string | undefined
  missedSessionStart: string | undefined
  missedClassSemester: string | undefined
  currentSemester: string
  reopenWithoutSavedFilterDate: boolean
  now: Dayjs
}): Dayjs => {
  if (makeupSessionStart && !reopenWithoutSavedFilterDate) {
    return setDayjsTimeToZero(dayjs(makeupSessionStart))
  }
  if (
    reopenWithoutSavedFilterDate ||
    !missedSessionStart ||
    missedClassSemester === currentSemester
  ) {
    return setDayjsTimeToZero(now)
  }
  return setDayjsTimeToZero(dayjs(missedSessionStart))
}

/**
 * The semesters whose enrollments must be in the conflict pool.
 *
 * Derived from the candidate classes the picker actually loaded, so the pool
 * follows the candidate query by construction: every candidate session belongs
 * to one of `candidateClasses` (`possibleMakeupClassSessions` filters on their
 * ids, and the persisted initial session is only merged in when its class is
 * among them), so any semester a candidate can come from is in this list. If a
 * curriculum ever spans terms, or the candidate query changes shape, the pool
 * widens on its own instead of relying on a rule restated here.
 *
 * The missed semester and the one after it are always included as well. That is
 * not a correctness claim: it keeps the pool identical to what it was before the
 * derivation, and pre-warms the next-semester toggle so its enrollments are
 * already loaded when the candidate classes for it resolve. Without it, there
 * would be one Firestore round trip in which next-semester candidates are
 * offered against a pool that has not yet loaded that term.
 *
 * A candidate class with no `semester` contributes nothing here;
 * `selectStudentClassSessionIds` fails open on the student's unlabelled classes
 * independently, so an unlabelled enrollment is never dropped.
 *
 * Empty until the missed class resolves (the candidate query does not run
 * before it does), so only the missed class loads until then. `addOneSemester`
 * defaults to the CURRENT semester when passed nothing, which would scope to
 * the wrong term, hence the guard.
 *
 * Residual assumption, shared with every semester-labelled scope: two classes
 * that run at the same time carry the same semester label. `SEMESTER_DATES`
 * is maintained to match actual term starts, so a "2026 A" class does not
 * overlap a "2025 C" one in practice.
 */
export const selectConflictSemesters = ({
  missedClass,
  candidateClasses,
}: {
  missedClass: Pick<ClassConstructor, "semester"> | null | undefined
  candidateClasses: Array<Pick<ClassConstructor, "semester">>
}): string[] => {
  const missedSemester = missedClass?.semester

  return uniq(
    compact([
      missedSemester,
      missedSemester ? addOneSemester(missedSemester) : undefined,
      ...candidateClasses.map((classObj) => classObj.semester),
    ])
  ).sort()
}

/**
 * Which of the student's class sessions the makeup form needs to load.
 *
 * These sessions exist only to time-conflict-check picker candidates, so the
 * pool only needs the semesters candidates can come from — `semesters` is
 * produced by `selectConflictSemesters` above. A student's other semesters are
 * pure overhead.
 *
 * Deliberately NOT scoped by the picker's date window, even though that would
 * load less. The window moves every time the user edits the filter date, so
 * keying the subscription to it re-subscribes mid-edit and clobbers the
 * uncommitted DatePicker text, and it also lets the option builder run against a
 * pool holding only the missed class — auto-select could then land on a session
 * that becomes a conflict a moment later. Semester scope is stable for the whole
 * life of the dialog.
 *
 * - The missed class is always included; its sessions feed the missed-session
 *   dropdown, which has no window at all.
 * - A class with no semester fails open and is included, since it cannot be
 *   proven irrelevant.
 *
 * Without this a two-year student loaded every session of every class they had
 * ever attended (100-250 docs across 4-9 `in` listeners) to conflict-check two
 * weeks of candidates.
 */
export const selectStudentClassSessionIds = ({
  studentClasses,
  missedClassId,
  semesters,
}: {
  studentClasses: ClassConstructor[]
  missedClassId: Uuid | null | undefined
  semesters: string[]
}): Uuid[] => {
  const relevantSemesters = new Set(compact(semesters))

  return studentClasses
    .filter((classObj) => {
      if (missedClassId && classObj.id === missedClassId) return true
      if (!classObj.semester) return true
      return relevantSemesters.has(classObj.semester)
    })
    .flatMap((classObj) => classObj.sessionIds ?? [])
}
